import React, { useState } from 'react';
import {
  Box, List, ListItem, ListItemIcon, ListItemText,
  Avatar, Typography, Tooltip, IconButton, alpha, Divider,
} from '@mui/material';
import {
  Videocam, CardMembership, History, DirectionsCar,
  Settings, ChevronLeft, ChevronRight,
  Group, Assessment, Dashboard, TableChart, AttachMoney,
  ScheduleSend, DirectionsRun, RoomService,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigation, VIEWS } from '../../context/NavigationContext';
import SelectorParqueadero from './SelectorParqueadero';

const SIDEBAR_W  = 228;
const SIDEBAR_CL = 64;
const PRIMARY    = '#282626';
const ORANGE     = '#F59E0B';
const WHITE      = '#FFFFFF';

const ROLE_LABEL = {
  admin: 'Administrador', supervisor: 'Supervisor',
  guard: 'Seguridad', operator: 'Operador', superadmin: 'Super Admin',
};

export default function Sidebar() {
  const { user } = useAuth();
  const { currentView, navigate } = useNavigation();
  const [open, setOpen] = useState(true);

  const isAdmin      = ['admin', 'superadmin'].includes(user?.role);
  const isSupervisor = ['admin', 'superadmin', 'supervisor'].includes(user?.role);
  const width = open ? SIDEBAR_W : SIDEBAR_CL;

  const sections = [
    {
      items: [
        { label: 'Dashboard',           icon: <Dashboard />,       view: VIEWS.DASHBOARD,          show: true,        desc: 'Gráficas y estadísticas' },
        { label: 'Bitácora',            icon: <TableChart />,      view: VIEWS.BITACORA,           show: true,        desc: 'Registro de vehículos' },
      ]
    },
    {
      label: 'Operaciones',
      items: [
        { label: 'Control de Cámaras',  icon: <Videocam />,        view: VIEWS.CONTROL_MANUAL,     show: true,        desc: 'Abrir/cerrar barrera' },
        { label: 'Historial de cierre',  icon: <History />,         view: VIEWS.CAMBIO_TURNO,       show: isSupervisor, desc: 'Historial de cierres de caja' },
        { label: 'Salidas Automáticas', icon: <ScheduleSend />,    view: VIEWS.SALIDAS_PROGRAMADAS, show: isAdmin,    desc: 'Sacar vehículos a una hora fija' },
        { label: 'Escapes',             icon: <DirectionsRun />,   view: VIEWS.ESCAPES,             show: isAdmin,    desc: 'Cobros no realizados por error' },
      ]
    },
    {
      label: 'Gestión',
      items: [
        { label: 'Suscriptores',        icon: <CardMembership />,  view: VIEWS.SUSCRIPTORES,       show: isAdmin,     desc: 'Planes de suscripción' },
        { label: 'Placas Autorizadas',  icon: <DirectionsCar />,   view: VIEWS.PLACAS_AUTORIZADAS, show: isAdmin,     desc: 'Gestionar placas' },
        { label: 'Servicios',           icon: <RoomService />,     view: VIEWS.SERVICIOS,          show: isAdmin,     desc: 'Lavado, aspirado y otros servicios' },
        { label: 'Usuarios',            icon: <Group />,           view: VIEWS.USUARIOS,           show: isAdmin,     desc: 'Administrar usuarios' },
        { label: 'Reportería',          icon: <Assessment />,      view: VIEWS.REPORTERIA,         show: isSupervisor, desc: 'Exportar reportes' },
      ]
    },
    {
      label: 'Sistema',
      items: [
        { label: 'Tarifas',             icon: <AttachMoney />,     view: VIEWS.TARIFAS,            show: isAdmin,     desc: 'Precios y reglas de cobro' },
        { label: 'Configuraciones',     icon: <Settings />,        view: VIEWS.CONFIGURACIONES,    show: isAdmin,     desc: 'Ajustes del sistema' },
      ]
    },
  ];

  const NavItem = ({ item }) => {
    const active = currentView === item.view;
    return (
      <Tooltip title={open ? '' : `${item.label} — ${item.desc}`} placement="right" arrow>
        <ListItem
          onClick={() => navigate(item.view)}
          sx={{
            borderRadius: 2, mb: 0.25, px: open ? 1.5 : 1, py: 0.85,
            cursor: 'pointer',
            bgcolor: active ? alpha(ORANGE, 0.15) : 'transparent',
            color: active ? WHITE : alpha(WHITE, 0.65),
            transition: 'all 0.15s',
            justifyContent: open ? 'flex-start' : 'center',
            '&:hover': {
              bgcolor: active ? alpha(ORANGE, 0.2) : alpha(WHITE, 0.06),
              color: WHITE,
              '& .MuiListItemIcon-root': { color: active ? ORANGE : alpha(WHITE, 0.9) },
            },
          }}
        >
          <ListItemIcon sx={{
            color: active ? ORANGE : alpha(WHITE, 0.5),
            minWidth: open ? 34 : 'auto',
            transition: 'color 0.15s',
          }}>
            {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 700 : 500, color: 'inherit', noWrap: true }}
            />
          )}
          {open && active && (
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: ORANGE, flexShrink: 0 }} />
          )}
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <Box sx={{
      width, minWidth: width, bgcolor: PRIMARY,
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.25s ease, min-width 0.25s ease',
      overflowX: 'hidden', overflowY: 'auto',
      flexShrink: 0,
      position: 'sticky', top: 0,
    }}>
      {/* Cabecera del sidebar: logo del cliente */}
      <Box sx={{
        px: open ? 2 : 0, py: 1.75,
        display: 'flex', alignItems: 'center',
        justifyContent: open ? 'space-between' : 'center',
        borderBottom: `1px solid rgba(255,255,255,0.08)`,
        minHeight: 68,
      }}>
        <SelectorParqueadero abierto={open} />
        <IconButton size="small" onClick={() => setOpen(v => !v)}
          sx={{ color: alpha(WHITE, 0.45), '&:hover': { color: WHITE }, flexShrink: 0 }}>
          {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Box>

      {/* Navegación */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: open ? 1 : 0.5, py: 1 }}>
        {sections.map((section, si) => {
          const visible = section.items.filter(i => i.show);
          if (!visible.length) return null;
          return (
            <Box key={si} sx={{ mb: 1 }}>
              {open && section.label && (
                <Typography variant="caption" sx={{
                  color: alpha(WHITE, 0.3), fontSize: 9, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 1.2,
                  px: 1.5, py: 0.75, display: 'block',
                }}>
                  {section.label}
                </Typography>
              )}
              {!open && si > 0 && <Divider sx={{ borderColor: alpha(WHITE, 0.08), my: 0.5 }} />}
              <List disablePadding>
                {visible.map(item => <NavItem key={item.view} item={item} />)}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* Usuario al fondo */}
      <Box sx={{ p: open ? 2 : 1, borderTop: `1px solid rgba(255,255,255,0.08)` }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(ORANGE, 0.25), color: ORANGE, fontSize: 12, fontWeight: 700 }}>
              {user?.name?.charAt(0)}{user?.lastname?.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: WHITE, display: 'block', lineHeight: 1.3 }} noWrap>
                {user?.name} {user?.lastname}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(WHITE, 0.45), fontSize: 10 }}>
                {ROLE_LABEL[user?.role] || user?.role}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Tooltip title={`${user?.name} — ${ROLE_LABEL[user?.role] || ''}`} placement="right">
            <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(ORANGE, 0.25), color: ORANGE, fontSize: 12, fontWeight: 700, mx: 'auto' }}>
              {user?.name?.charAt(0)}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
